import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Crown, Lock, Play, FileText, Users, MessageSquare, Settings, LogOut, 
  Menu, X, Check, Shield, CreditCard, Upload, Eye, EyeOff, Loader2,
  ChevronRight, Star, Zap, Award, Calendar, Clock, Heart, Share2,
  Gift, Copy, Trophy, Percent, Ticket, Bell, Camera, Video, Radio,
  DollarSign, TrendingUp, Headphones, MapPin, Phone, Mail, Sparkles,
  Send, Image, AlertTriangle, ArrowLeft, BarChart3, UserCheck, UserX,
  Trash2, Edit, Search, Filter, ChevronLeft, MoreVertical,
  Pause, Volume2, VolumeX, Maximize, Minimize
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// SECURITY PROTECTION HOOK
// ============================================
const useSecurityProtection = () => {
  useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable F12 (Developer Tools)
      if (e.key === 'F12' || e.keyCode === 123) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+I (Developer Tools)
      if (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'i' || e.keyCode === 73)) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && (e.key === 'J' || e.key === 'j' || e.keyCode === 74)) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+Shift+C (Inspect Element)
      if (e.ctrlKey && e.shiftKey && (e.key === 'C' || e.key === 'c' || e.keyCode === 67)) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+U (View Source)
      if (e.ctrlKey && (e.key === 'U' || e.key === 'u' || e.keyCode === 85)) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+S (Save Page)
      if (e.ctrlKey && (e.key === 'S' || e.key === 's' || e.keyCode === 83)) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+P (Print)
      if (e.ctrlKey && (e.key === 'P' || e.key === 'p' || e.keyCode === 80)) {
        e.preventDefault();
        return false;
      }
      
      // Disable Ctrl+A (Select All)
      if (e.ctrlKey && (e.key === 'A' || e.key === 'a' || e.keyCode === 65)) {
        // Allow in input fields
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          return false;
        }
      }
      
      // Disable Ctrl+C (Copy) except in input fields
      if (e.ctrlKey && (e.key === 'C' || e.key === 'c' || e.keyCode === 67)) {
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          return false;
        }
      }
      
      // Disable PrintScreen
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        e.preventDefault();
        // Clear clipboard
        navigator.clipboard.writeText('').catch(() => {});
        return false;
      }
    };

    // Disable drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable copy
    const handleCopy = (e) => {
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        return false;
      }
    };

    // Disable cut
    const handleCut = (e) => {
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        return false;
      }
    };

    // Disable paste of images
    const handlePaste = (e) => {
      // Allow text paste in input fields only
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        return false;
      }
    };

    // Detect DevTools opening (basic detection)
    const detectDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;
      
      if (widthThreshold || heightThreshold) {
        // DevTools might be open - could redirect or show warning
        console.clear();
      }
    };

    // Disable text selection via selectstart
    const handleSelectStart = (e) => {
      if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        return false;
      }
    };

    // Add all event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('selectstart', handleSelectStart);
    
    // DevTools detection interval
    const devToolsInterval = setInterval(detectDevTools, 1000);

    // Disable right-click on images specifically
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      img.addEventListener('contextmenu', handleContextMenu);
      img.addEventListener('dragstart', handleDragStart);
    });

    // Clear console periodically
    const consoleClearInterval = setInterval(() => {
      console.clear();
    }, 5000);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('selectstart', handleSelectStart);
      clearInterval(devToolsInterval);
      clearInterval(consoleClearInterval);
    };
  }, []);
};

// Security wrapper component
const SecurityWrapper = ({ children }) => {
  useSecurityProtection();
  
  return (
    <div 
      className="secure-content watermark-global"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      {children}
    </div>
  );
};

// ============================================
// END SECURITY PROTECTION
// ============================================

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // GoogleAuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true  // Send cookies for Emergent Auth
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <nav className="fixed top-0 w-full z-50 glass border-b border-white/5" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Crown className="w-8 h-8 text-[#D4AF37]" />
            <span className="font-serif text-xl gold-text font-bold">Élixir Noir</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/articles" className="nav-link" data-testid="nav-articles">Articles</Link>
            <Link to="/videos" className="nav-link" data-testid="nav-videos">Vidéos</Link>
            <Link to="/forum" className="nav-link" data-testid="nav-forum">Forum</Link>
            <Link to="/pricing" className="nav-link" data-testid="nav-pricing">Abonnements</Link>
            <Link to="/become-creator" className="nav-link text-[#D4AF37]" data-testid="nav-become-creator">Devenir Créateur</Link>
            
            {user ? (
              <div className="flex items-center gap-4">
                <NotificationsDropdown />
                <Link to="/messages" className="relative p-2 hover:bg-white/5 rounded-lg transition-colors" data-testid="nav-messages">
                  <MessageSquare className="w-5 h-5" />
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin" className="relative p-2 hover:bg-white/5 rounded-lg transition-colors text-[#D4AF37]" data-testid="nav-admin">
                    <Shield className="w-5 h-5" />
                  </Link>
                )}
                <Link to="/dashboard" className="btn-outline text-sm py-2 px-4" data-testid="nav-dashboard">
                  Dashboard
                </Link>
                <button onClick={logout} className="nav-link flex items-center gap-1" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" /> Déconnexion
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <Link to="/login" className="nav-link" data-testid="nav-login">Connexion</Link>
                <Link to="/register" className="btn-gold text-sm py-2 px-6" data-testid="nav-register">
                  S'inscrire
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/5">
            <div className="flex flex-col gap-4">
              <Link to="/articles" className="nav-link py-2" onClick={() => setIsOpen(false)}>Articles</Link>
              <Link to="/videos" className="nav-link py-2" onClick={() => setIsOpen(false)}>Vidéos</Link>
              <Link to="/forum" className="nav-link py-2" onClick={() => setIsOpen(false)}>Forum</Link>
              <Link to="/pricing" className="nav-link py-2" onClick={() => setIsOpen(false)}>Abonnements</Link>
              <Link to="/become-creator" className="nav-link py-2 text-[#D4AF37]" onClick={() => setIsOpen(false)}>Devenir Créateur</Link>
              {user ? (
                <>
                  <Link to="/dashboard" className="nav-link py-2" onClick={() => setIsOpen(false)}>Dashboard</Link>
                  <Link to="/referral" className="nav-link py-2" onClick={() => setIsOpen(false)}>Parrainage</Link>
                  <button onClick={() => { logout(); setIsOpen(false); }} className="nav-link py-2 text-left">Déconnexion</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link py-2" onClick={() => setIsOpen(false)}>Connexion</Link>
                  <Link to="/register" className="btn-gold text-center mt-2" onClick={() => setIsOpen(false)}>S'inscrire</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Age Gate Component
const AgeGate = ({ onConfirm }) => {
  return (
    <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center p-4" data-testid="age-gate">
      <div className="max-w-md w-full text-center animate-fade-in-up">
        <Crown className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
        <h1 className="font-serif text-3xl gold-text mb-4">Élixir Noir</h1>
        <p className="text-[#A1A1AA] mb-8">
          Ce site est réservé aux personnes majeures (18 ans et plus).
          En continuant, vous confirmez avoir l'âge légal requis.
        </p>
        <div className="flex flex-col gap-4">
          <button 
            onClick={onConfirm}
            className="btn-gold w-full"
            data-testid="age-confirm-btn"
          >
            J'ai 18 ans ou plus
          </button>
          <a 
            href="https://google.com" 
            className="btn-outline w-full text-center"
            data-testid="age-deny-btn"
          >
            Je suis mineur(e)
          </a>
        </div>
        <p className="text-xs text-[#A1A1AA] mt-6">
          En accédant à ce site, vous acceptez nos conditions d'utilisation.
        </p>
      </div>
    </div>
  );
};

// Landing Page
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex items-center justify-center pt-16">
        <div className="max-w-4xl mx-auto px-4 text-center animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-8">
            <Star className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-sm text-[#A1A1AA]">Plateforme Premium Exclusive</span>
          </div>
          
          <h1 className="font-serif text-5xl md:text-7xl mb-6 leading-tight">
            Bienvenue dans<br />
            <span className="gold-text">l'Élixir Noir</span>
          </h1>
          
          <p className="text-xl text-[#A1A1AA] mb-12 max-w-2xl mx-auto leading-relaxed">
            Une communauté exclusive pour les connaisseurs. Accédez à du contenu premium,
            des vidéos exclusives et rejoignez un cercle privé de passionnés.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {user ? (
              <button 
                onClick={() => navigate('/dashboard')}
                className="btn-gold flex items-center justify-center gap-2"
                data-testid="hero-dashboard-btn"
              >
                Accéder au Dashboard <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <>
                <button 
                  onClick={() => navigate('/register')}
                  className="btn-gold flex items-center justify-center gap-2"
                  data-testid="hero-register-btn"
                >
                  Commencer Maintenant <ChevronRight className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => navigate('/pricing')}
                  className="btn-outline"
                  data-testid="hero-pricing-btn"
                >
                  Voir les Abonnements
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-center mb-16">
            Pourquoi rejoindre <span className="gold-text">l'Élixir</span> ?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Play className="w-8 h-8" />}
              title="Vidéos Exclusives"
              description="Accédez à une bibliothèque de contenus vidéo premium, régulièrement mise à jour."
            />
            <FeatureCard 
              icon={<FileText className="w-8 h-8" />}
              title="Articles Premium"
              description="Des articles approfondis et des guides exclusifs réservés à nos membres."
            />
            <FeatureCard 
              icon={<Users className="w-8 h-8" />}
              title="Communauté Privée"
              description="Rejoignez notre forum exclusif et échangez avec d'autres passionnés."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-[#0F0F0F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl mb-6">
            Prêt à rejoindre <span className="gold-text">l'élite</span> ?
          </h2>
          <p className="text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
            Inscrivez-vous maintenant et découvrez un monde de contenu exclusif.
            Vérification d'identité sécurisée et accès instantané.
          </p>
          <button 
            onClick={() => navigate('/register')}
            className="btn-gold animate-pulse-gold"
            data-testid="cta-register-btn"
          >
            Créer Mon Compte Premium
          </button>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-[#0F0F0F] border border-white/5 p-8 hover:border-[#D4AF37]/50 transition-all duration-300 hover:-translate-y-1" data-testid={`feature-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-[#D4AF37] mb-4">{icon}</div>
    <h3 className="font-serif text-xl mb-3">{title}</h3>
    <p className="text-[#A1A1AA] text-sm leading-relaxed">{description}</p>
  </div>
);

// Register Page
const RegisterPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    date_of_birth: '',
    document_type: 'id_card',
    document_number: '',
    document_file: null
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    // Calculate age
    const dob = new Date(formData.date_of_birth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;

    if (age < 18) {
      toast.error("Vous devez avoir 18 ans ou plus");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        date_of_birth: formData.date_of_birth
      });
      
      login(response.data.token, response.data.user);
      toast.success("Compte créé avec succès !");
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStep2 = async (e) => {
    e.preventDefault();
    
    if (!formData.document_file) {
      toast.error("Veuillez télécharger votre pièce d'identité");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('document_type', formData.document_type);
      data.append('document_number', formData.document_number);
      data.append('file', formData.document_file);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/verify-id`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        params: {
          document_type: formData.document_type,
          document_number: formData.document_number
        }
      });
      
      toast.success("Identité vérifiée !");
      navigate('/pricing');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la vérification");
    } finally {
      setLoading(false);
    }
  };

  const skipVerification = () => {
    toast.info("Vous pourrez vérifier votre identité plus tard");
    navigate('/pricing');
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="register-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">Créer votre compte</h1>
          <p className="text-[#A1A1AA]">
            {step === 1 ? "Étape 1 : Informations personnelles" : "Étape 2 : Vérification d'identité"}
          </p>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          <div className={`flex-1 h-1 ${step >= 1 ? 'bg-[#D4AF37]' : 'bg-white/10'}`} />
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-[#D4AF37]' : 'bg-white/10'}`} />
        </div>

        {step === 1 ? (
          <form onSubmit={handleSubmitStep1} className="space-y-4" data-testid="register-form-step1">
            <div>
              <label className="block text-sm mb-2">Nom complet</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="Jean Dupont"
                required
                data-testid="register-fullname"
              />
            </div>
            
            <div>
              <label className="block text-sm mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="votre@email.com"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">Date de naissance</label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth}
                onChange={handleChange}
                className="input-dark w-full"
                required
                data-testid="register-dob"
              />
              <p className="text-xs text-[#A1A1AA] mt-1">Vous devez avoir 18 ans ou plus</p>
            </div>

            <div>
              <label className="block text-sm mb-2">Mot de passe</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="input-dark w-full pr-10"
                  placeholder="••••••••"
                  required
                  minLength={8}
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="••••••••"
                required
                data-testid="register-confirm-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full flex items-center justify-center gap-2"
              data-testid="register-submit-step1"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Continuer
            </button>

            {/* Social Login Options */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#27272A]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#18181B] text-[#A1A1AA]">Ou s'inscrire avec</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-all duration-200 font-medium"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Inscription avec Google
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmitStep2} className="space-y-4" data-testid="register-form-step2">
            <div className="bg-[#0F0F0F] border border-white/10 p-4 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                <div>
                  <h4 className="font-medium mb-1">Vérification sécurisée</h4>
                  <p className="text-xs text-[#A1A1AA]">
                    Vos documents sont cryptés et stockés de manière sécurisée.
                    Cette étape permet de garantir l'accès aux adultes uniquement.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm mb-2">Type de document</label>
              <select
                name="document_type"
                value={formData.document_type}
                onChange={handleChange}
                className="input-dark w-full"
                data-testid="register-doc-type"
              >
                <option value="id_card">Carte d'identité</option>
                <option value="passport">Passeport</option>
                <option value="driver_license">Permis de conduire</option>
              </select>
            </div>

            <div>
              <label className="block text-sm mb-2">Numéro du document</label>
              <input
                type="text"
                name="document_number"
                value={formData.document_number}
                onChange={handleChange}
                className="input-dark w-full"
                placeholder="Ex: 123456789"
                required
                data-testid="register-doc-number"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">Photo du document</label>
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-white/10 p-8 cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                <Upload className="w-8 h-8 text-[#A1A1AA] mb-2" />
                <span className="text-sm text-[#A1A1AA]">
                  {formData.document_file ? formData.document_file.name : "Cliquez pour télécharger"}
                </span>
                <input
                  type="file"
                  name="document_file"
                  onChange={handleChange}
                  accept="image/*,.pdf"
                  className="hidden"
                  data-testid="register-doc-file"
                />
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full flex items-center justify-center gap-2"
              data-testid="register-submit-step2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Vérifier mon identité
            </button>

            <button
              type="button"
              onClick={skipVerification}
              className="btn-outline w-full"
              data-testid="register-skip-verification"
            >
              Passer cette étape
            </button>
          </form>
        )}

        {step === 1 && (
          <p className="text-center text-sm text-[#A1A1AA] mt-6">
            Déjà membre ?{' '}
            <Link to="/login" className="text-[#D4AF37] hover:underline" data-testid="register-login-link">
              Connectez-vous
            </Link>
          </p>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '', code: '' });
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (requires2FA) {
        const response = await axios.post(`${API}/auth/verify-2fa`, {
          email: formData.email,
          code: formData.code
        });
        login(response.data.token, response.data.user);
        toast.success("Connexion réussie !");
        navigate('/dashboard');
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });

        if (response.data.requires_2fa) {
          setRequires2FA(true);
          toast.info("Code 2FA requis");
        } else {
          login(response.data.token, response.data.user);
          toast.success("Connexion réussie !");
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-2">Connexion</h1>
          <p className="text-[#A1A1AA]">
            {requires2FA ? "Entrez votre code 2FA" : "Accédez à votre espace membre"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
          {!requires2FA ? (
            <>
              <div>
                <label className="block text-sm mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="input-dark w-full"
                  placeholder="votre@email.com"
                  required
                  data-testid="login-email"
                />
              </div>

              <div>
                <label className="block text-sm mb-2">Mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-dark w-full pr-10"
                    placeholder="••••••••"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div>
              <label className="block text-sm mb-2">Code d'authentification</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                className="input-dark w-full text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                required
                data-testid="login-2fa-code"
              />
              <p className="text-xs text-[#A1A1AA] mt-2 text-center">
                Entrez le code de votre application d'authentification
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-gold w-full flex items-center justify-center gap-2"
            data-testid="login-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {requires2FA ? "Vérifier" : "Se connecter"}
          </button>
        </form>

        {!requires2FA && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#27272A]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#18181B] text-[#A1A1AA]">Ou continuer avec</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/dashboard';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-all duration-200 font-medium"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Connexion avec Google
            </button>
          </>
        )}

        <p className="text-center text-sm text-[#A1A1AA] mt-6">
          Pas encore membre ?{' '}
          <Link to="/register" className="text-[#D4AF37] hover:underline" data-testid="login-register-link">
            Créez votre compte
          </Link>
        </p>
      </div>
      <Footer />
    </div>
  );
};

// Pricing Page
const PricingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      id: 'basic',
      name: 'Basic',
      price: '9.94',
      period: '/mois',
      features: ['Accès aux articles', 'Forum communauté', 'Support email'],
      popular: false
    },
    {
      id: 'premium',
      name: 'Premium',
      price: '19.94',
      period: '/mois',
      features: ['Tout Basic', 'Vidéos exclusives', 'Chat privé', 'Support prioritaire'],
      popular: true
    },
    {
      id: 'vip',
      name: 'VIP',
      price: '49.94',
      period: '/mois',
      features: ['Tout Premium', 'Contenu 4K', 'Événements live', 'Accès anticipé', 'Support VIP 24/7'],
      popular: false
    },
    {
      id: 'lifetime',
      name: 'Accès à Vie',
      price: '299.94',
      period: 'unique',
      features: ['Accès VIP permanent', 'Pas de renouvellement', 'Bonus exclusifs', 'Priorité totale'],
      popular: false
    }
  ];

  const handleSubscribe = async (planId, paymentMethod = 'stripe') => {
    if (!user) {
      toast.info("Veuillez vous connecter pour vous abonner");
      navigate('/register');
      return;
    }

    setLoading(planId);
    try {
      if (paymentMethod === 'paypal') {
        // PayPal checkout
        const plan = plans.find(p => p.id === planId);
        const response = await axios.post(`${API}/payments/paypal/create-order`, {
          subscription_tier: planId,
          amount: parseFloat(plan.price),
          currency: 'EUR'
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Redirect to PayPal approval URL
        window.location.href = response.data.approval_url;
      } else {
        // Stripe checkout (existing)
        const response = await axios.post(`${API}/subscriptions/checkout`, {
          plan_id: planId,
          origin_url: window.location.origin
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16 animate-fade-in-up">
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Choisissez votre <span className="gold-text">abonnement</span>
          </h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Débloquez l'accès à tout notre contenu exclusif. Annulez à tout moment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative p-6 ${
                plan.popular ? 'premium-card' : 'bg-[#0F0F0F] border border-white/5'
              } hover:-translate-y-2 transition-all duration-300`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`pricing-plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-xs font-bold px-4 py-1 uppercase tracking-wider">
                  Populaire
                </div>
              )}

              <h3 className="font-serif text-2xl mb-2">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-bold gold-text">{plan.price}€</span>
                <span className="text-[#A1A1AA] text-sm">{plan.period}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#D4AF37]" />
                    {feature}
                  </li>
                ))}
              </ul>

              <div className="space-y-2">
                <button
                  onClick={() => handleSubscribe(plan.id, 'stripe')}
                  disabled={loading === plan.id || user?.subscription_tier === plan.id}
                  className={`w-full ${plan.popular ? 'btn-gold' : 'btn-outline'} flex items-center justify-center gap-2`}
                  data-testid={`subscribe-stripe-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : user?.subscription_tier === plan.id ? (
                    'Abonnement actuel'
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      Payer avec Stripe
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => handleSubscribe(plan.id, 'paypal')}
                  disabled={loading === plan.id || user?.subscription_tier === plan.id}
                  className="w-full bg-[#0070BA] hover:bg-[#005EA6] text-white py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium disabled:opacity-50"
                  data-testid={`subscribe-paypal-${plan.id}`}
                >
                  {loading === plan.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : user?.subscription_tier === plan.id ? (
                    'Abonnement actuel'
                  ) : (
                    <>
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 0 0-.794.68l-.04.22-.63 3.993-.032.17a.804.804 0 0 1-.794.679H7.72a.483.483 0 0 1-.477-.558L9.28 7.13a.946.946 0 0 1 .933-.8h5.45c1.828 0 3.067.381 3.8 1.172.434.467.683 1.103.605 1.976zm-.844-2.589C18.154 4.365 16.15 3.7 13.36 3.7H6.585a.95.95 0 0 0-.936.788L3.079 20.526a.58.58 0 0 0 .572.674h4.145l1.04-6.591-.032.202a.944.944 0 0 1 .933-.8h1.943c3.82 0 6.816-1.553 7.689-6.043.028-.149.053-.294.073-.436.273-1.78-.001-2.992-.944-4.044-.22-.244-.488-.463-.785-.649z"/>
                      </svg>
                      Payer avec PayPal
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-[#A1A1AA]">
          <p className="flex items-center justify-center gap-2">
            <Shield className="w-4 h-4 text-[#D4AF37]" />
            Paiements sécurisés par Stripe et PayPal. Vos données sont protégées.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Payment Success Page
const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const { token, fetchUser } = useAuth();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus();
    }
  }, [sessionId, token]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/subscriptions/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        await fetchUser();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      if (attempts < maxAttempts) {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="animate-fade-in-up">
            <Loader2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl mb-4">Vérification du paiement...</h1>
            <p className="text-[#A1A1AA]">Veuillez patienter quelques instants.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-fade-in-up">
            <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-black" />
            </div>
            <h1 className="font-serif text-3xl mb-4">Paiement réussi !</h1>
            <p className="text-[#A1A1AA] mb-8">
              Bienvenue dans l'Élixir Noir. Votre abonnement {paymentData?.metadata?.plan_name} est maintenant actif.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-gold"
              data-testid="payment-success-dashboard"
            >
              Accéder au Dashboard
            </button>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div className="animate-fade-in-up">
            <X className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-serif text-3xl mb-4">Erreur de paiement</h1>
            <p className="text-[#A1A1AA] mb-8">
              {status === 'expired' 
                ? "La session de paiement a expiré."
                : "Une erreur est survenue lors de la vérification du paiement."
              }
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="btn-outline"
              data-testid="payment-error-retry"
            >
              Réessayer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTierBadge = (tier) => {
    const badges = {
      basic: { class: 'tier-basic', label: 'Basic' },
      premium: { class: 'tier-premium', label: 'Premium' },
      vip: { class: 'tier-vip', label: 'VIP' },
      lifetime: { class: 'tier-lifetime', label: 'Lifetime' }
    };
    return badges[tier] || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const tierBadge = getTierBadge(user?.subscription_tier);

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12 animate-fade-in-up">
          <div className="flex items-center gap-4 mb-4">
            <h1 className="font-serif text-3xl md:text-4xl">
              Bienvenue, <span className="gold-text">{user?.full_name}</span>
            </h1>
            {tierBadge && (
              <span className={`${tierBadge.class} px-3 py-1 text-xs font-bold uppercase tracking-wider text-black`}>
                {tierBadge.label}
              </span>
            )}
          </div>
          <p className="text-[#A1A1AA]">Votre espace membre exclusif</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <StatCard icon={<FileText />} label="Articles" value={stats?.articles_count || 0} />
          <StatCard icon={<Play />} label="Vidéos" value={stats?.videos_count || 0} />
          <StatCard icon={<MessageSquare />} label="Posts Forum" value={stats?.posts_count || 0} />
          <StatCard icon={<Crown />} label="Abonnement" value={user?.subscription_tier || 'Aucun'} />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <QuickAction
            icon={<Play className="w-6 h-6" />}
            title="Vidéos"
            description="Regardez les dernières vidéos exclusives"
            onClick={() => navigate('/videos')}
            testId="dashboard-videos"
          />
          <QuickAction
            icon={<FileText className="w-6 h-6" />}
            title="Articles"
            description="Lisez nos articles premium"
            onClick={() => navigate('/articles')}
            testId="dashboard-articles"
          />
          <QuickAction
            icon={<Users className="w-6 h-6" />}
            title="Communauté"
            description="Rejoignez les discussions"
            onClick={() => navigate('/forum')}
            testId="dashboard-forum"
          />
          <QuickAction
            icon={<Gift className="w-6 h-6" />}
            title="Parrainage"
            description="Invitez des amis, gagnez des récompenses"
            onClick={() => navigate('/referral')}
            testId="dashboard-referral"
          />
        </div>

        {/* Referral Banner */}
        {stats && (
          <div className="premium-card p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Gift className="w-10 h-10 text-[#D4AF37]" />
              <div>
                <h3 className="font-serif text-lg">Programme de Parrainage</h3>
                <p className="text-sm text-[#A1A1AA]">
                  Vous avez <span className="text-[#D4AF37] font-bold">{stats.referral_points || 0}</span> points et 
                  <span className="text-[#D4AF37] font-bold"> {stats.referral_count || 0}</span> filleuls
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/referral')}
              className="btn-gold text-sm whitespace-nowrap"
              data-testid="dashboard-referral-cta"
            >
              Voir mes récompenses
            </button>
          </div>
        )}

        {/* Account Section */}
        <div className="bg-[#0F0F0F] border border-white/5 p-6">
          <h2 className="font-serif text-xl mb-6">Paramètres du compte</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-[#A1A1AA]">Email</label>
              <p className="font-mono">{user?.email}</p>
            </div>
            <div>
              <label className="text-sm text-[#A1A1AA]">Authentification 2FA</label>
              <p className="flex items-center gap-2">
                {user?.has_2fa ? (
                  <><Check className="w-4 h-4 text-green-500" /> Activée</>
                ) : (
                  <><X className="w-4 h-4 text-red-500" /> Non activée</>
                )}
              </p>
            </div>
            <div>
              <label className="text-sm text-[#A1A1AA]">Vérification d'identité</label>
              <p className="flex items-center gap-2">
                {user?.is_id_verified ? (
                  <><Check className="w-4 h-4 text-green-500" /> Vérifiée</>
                ) : (
                  <><X className="w-4 h-4 text-red-500" /> Non vérifiée</>
                )}
              </p>
            </div>
            <div>
              <label className="text-sm text-[#A1A1AA]">Membre depuis</label>
              <p>{user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}</p>
            </div>
          </div>

          {!user?.subscription_tier && (
            <div className="mt-6 p-4 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
              <p className="text-sm mb-3">
                Vous n'avez pas d'abonnement actif. Débloquez tout le contenu premium !
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="btn-gold text-sm"
                data-testid="dashboard-upgrade"
              >
                Voir les abonnements
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="bg-[#0F0F0F] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-colors">
    <div className="text-[#D4AF37] mb-2">{icon}</div>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-sm text-[#A1A1AA]">{label}</p>
  </div>
);

const QuickAction = ({ icon, title, description, onClick, testId }) => (
  <button
    onClick={onClick}
    className="bg-[#0F0F0F] border border-white/5 p-6 text-left hover:border-[#D4AF37]/50 hover:-translate-y-1 transition-all duration-300"
    data-testid={testId}
  >
    <div className="text-[#D4AF37] mb-4">{icon}</div>
    <h3 className="font-serif text-lg mb-2">{title}</h3>
    <p className="text-sm text-[#A1A1AA]">{description}</p>
  </button>
);

// Articles Page
const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/articles`, { headers });
      setArticles(response.data.articles);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="articles-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-serif text-4xl mb-8">
          <span className="gold-text">Articles</span> Premium
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Aucun article disponible pour le moment.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

const ArticleCard = ({ article }) => {
  const navigate = useNavigate();

  return (
    <div
      className={`bg-[#0F0F0F] border ${article.locked ? 'border-white/5' : 'border-[#D4AF37]/30'} p-6 hover:-translate-y-1 transition-all duration-300 cursor-pointer`}
      onClick={() => !article.locked && navigate(`/articles/${article.id}`)}
      data-testid={`article-card-${article.id}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-[#A1A1AA] uppercase tracking-wider">{article.category}</span>
        {article.is_premium && (
          <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-0.5">Premium</span>
        )}
      </div>
      
      <h3 className="font-serif text-xl mb-3">{article.title}</h3>
      <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-3">{article.excerpt}</p>
      
      <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
        <span>{article.author_name}</span>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {article.views}</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {article.likes}</span>
        </div>
      </div>

      {article.locked && (
        <div className="mt-4 flex items-center gap-2 text-[#D4AF37] text-sm">
          <Lock className="w-4 h-4" />
          <span>Abonnement requis</span>
        </div>
      )}
    </div>
  );
};

// Videos Page
const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/videos`, { headers });
      setVideos(response.data.videos);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="videos-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-serif text-4xl mb-8">
          <span className="gold-text">Vidéos</span> Exclusives
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video) => (
            <div key={video.id} className="space-y-4">
              <VideoCard video={video} />
              {/* Demo: Custom Video Player for premium content */}
              {video.is_premium && user?.subscription_tier && (
                <div className="mt-4">
                  <p className="text-[#D4AF37] text-sm mb-2 flex items-center gap-2">
                    <Crown className="w-4 h-4" />
                    Lecteur Premium Protégé
                  </p>
                  <CustomVideoPlayer
                    videoUrl={video.url}
                    thumbnailUrl={video.thumbnail}
                    title={video.title}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {videos.length === 0 && (
          <div className="text-center py-12">
            <Play className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Aucune vidéo disponible pour le moment.</p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

const VideoCard = ({ video }) => {
  const navigate = useNavigate();

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className={`bg-[#0F0F0F] border ${video.locked ? 'border-white/5' : 'border-[#D4AF37]/30'} overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer`}
      onClick={() => !video.locked && navigate(`/videos/${video.id}`)}
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative aspect-video">
        <img 
          src={video.thumbnail_url} 
          alt={video.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          {video.locked ? (
            <Lock className="w-12 h-12 text-[#D4AF37]" />
          ) : (
            <Play className="w-12 h-12 text-white" />
          )}
        </div>
        <span className="absolute bottom-2 right-2 bg-black/80 text-xs px-2 py-1">
          {formatDuration(video.duration)}
        </span>
        {video.is_premium && (
          <span className="absolute top-2 left-2 bg-[#D4AF37] text-black text-xs font-bold px-2 py-1">
            Premium
          </span>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-serif text-lg mb-2">{video.title}</h3>
        <p className="text-[#A1A1AA] text-sm mb-3 line-clamp-2">{video.description}</p>
        
        <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
          <span>{video.author_name}</span>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {video.views}</span>
            <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {video.likes}</span>
          </div>
        </div>
      </div>
    </div>
  );
};


// Custom Video Player with Protection
const CustomVideoPlayer = ({ videoUrl, thumbnailUrl, title }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeout = useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) {
      videoRef.current.currentTime = pos * duration;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoRef.current?.parentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current);
    }
    controlsTimeout.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  // Prevent right-click and downloads
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div 
      className="relative bg-black rounded-lg overflow-hidden no-download no-select"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      style={{ maxWidth: '100%', aspectRatio: '16/9' }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        poster={thumbnailUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onContextMenu={handleContextMenu}
        controlsList="nodownload nofullscreen noremoteplayback"
        disablePictureInPicture
        className="w-full h-full object-contain"
        playsInline
      />

      {/* Custom Controls Overlay */}
      <div 
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Play/Pause Overlay */}
        {!isPlaying && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={togglePlay}
              className="w-20 h-20 rounded-full bg-[#D4AF37]/90 hover:bg-[#D4AF37] transition-all flex items-center justify-center"
            >
              <Play className="w-10 h-10 text-black ml-1" />
            </button>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Progress Bar */}
          <div 
            className="w-full h-1 bg-white/20 rounded-full cursor-pointer mb-3 group"
            onClick={handleSeek}
          >
            <div 
              className="h-full bg-[#D4AF37] rounded-full transition-all relative"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#D4AF37] rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </div>

          <div className="flex items-center justify-between">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              <button onClick={togglePlay} className="text-white hover:text-[#D4AF37] transition-colors">
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
              
              <div className="flex items-center gap-2">
                <button onClick={toggleMute} className="text-white hover:text-[#D4AF37] transition-colors">
                  {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-20 accent-[#D4AF37]"
                />
              </div>

              <span className="text-white text-sm">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-3">
              <button onClick={toggleFullscreen} className="text-white hover:text-[#D4AF37] transition-colors">
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Watermark */}
        <div className="absolute top-4 right-4 text-white/20 text-xs font-bold pointer-events-none select-none">
          ÉLIXIR NOIR © {user?.email || 'PROTECTED'}
        </div>
      </div>
    </div>
  );
};


// Forum Page
const ForumPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/forum/posts`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="forum-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-serif text-4xl">
            <span className="gold-text">Forum</span> Communauté
          </h1>
          {user && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-gold text-sm"
              data-testid="forum-create-post"
            >
              Nouveau Post
            </button>
          )}
        </div>

        <div className="space-y-4">
          {posts.map((post) => (
            <div
              key={post.id}
              className="bg-[#0F0F0F] border border-white/5 p-6 hover:border-[#D4AF37]/30 transition-colors cursor-pointer"
              onClick={() => navigate(`/forum/${post.id}`)}
              data-testid={`forum-post-${post.id}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs bg-white/10 px-2 py-1">{post.category}</span>
              </div>
              <h3 className="font-serif text-xl mb-2">{post.title}</h3>
              <p className="text-[#A1A1AA] text-sm mb-4 line-clamp-2">{post.content}</p>
              
              <div className="flex items-center justify-between text-xs text-[#A1A1AA]">
                <span>Par {post.author_name}</span>
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> {post.comments_count}
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-3 h-3" /> {post.likes}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {posts.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
            <p className="text-[#A1A1AA]">Aucun post pour le moment. Soyez le premier !</p>
          </div>
        )}

        {showCreateModal && (
          <CreatePostModal onClose={() => setShowCreateModal(false)} onCreated={fetchPosts} />
        )}
      </div>
      <Footer />
    </div>
  );
};

const CreatePostModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({ title: '', content: '', category: 'Général' });
  const [loading, setLoading] = useState(false);
  const { token } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/forum/posts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Post créé !");
      onCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" data-testid="create-post-modal">
      <div className="bg-[#0F0F0F] border border-white/10 max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">Nouveau Post</h2>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm mb-2">Catégorie</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="input-dark w-full"
              data-testid="post-category"
            >
              <option value="Général">Général</option>
              <option value="Questions">Questions</option>
              <option value="Discussions">Discussions</option>
              <option value="Suggestions">Suggestions</option>
            </select>
          </div>

          <div>
            <label className="block text-sm mb-2">Titre</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="input-dark w-full"
              placeholder="Titre de votre post"
              required
              data-testid="post-title"
            />
          </div>

          <div>
            <label className="block text-sm mb-2">Contenu</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              className="input-dark w-full h-32 resize-none"
              placeholder="Écrivez votre message..."
              required
              data-testid="post-content"
            />
          </div>

          <div className="flex gap-4">
            <button type="button" onClick={onClose} className="btn-outline flex-1">
              Annuler
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="btn-gold flex-1 flex items-center justify-center gap-2"
              data-testid="post-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Publier
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // If user data passed from GoogleAuthCallback, allow access immediately
    if (location.state?.user) {
      setIsChecking(false);
      return;
    }

    // Check authentication via API (supports both JWT and session_token cookie)
    const checkAuth = async () => {
      if (!user && !loading) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            withCredentials: true  // Send session_token cookie
          });
          
          if (!response.data) {
            navigate('/login');
          }
        } catch (error) {
          navigate('/login');
        }
      }
      setIsChecking(false);
    };

    checkAuth();
  }, [user, loading, location, navigate, token]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return user ? children : null;
};

// Referral Page
const ReferralPage = () => {
  const { user, token } = useAuth();
  const [referralInfo, setReferralInfo] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyCode, setApplyCode] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchReferralInfo();
      fetchLeaderboard();
    }
  }, [token]);

  const fetchReferralInfo = async () => {
    try {
      const response = await axios.get(`${API}/referral/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralInfo(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/referral/leaderboard`);
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error(error);
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(referralInfo?.referral_code || '');
    toast.success("Code copié !");
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${referralInfo?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success("Lien de parrainage copié !");
  };

  const handleApplyCode = async (e) => {
    e.preventDefault();
    if (!applyCode.trim()) return;

    setApplyLoading(true);
    try {
      const response = await axios.post(`${API}/referral/apply`, 
        { referral_code: applyCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message);
      setApplyCode('');
      fetchReferralInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setApplyLoading(false);
    }
  };

  const handleRedeemPoints = async (rewardType) => {
    const pointsNeeded = referralInfo?.rewards_config?.video_discount_points || 50;
    
    if ((referralInfo?.referral_points || 0) < pointsNeeded) {
      toast.error(`Vous avez besoin de ${pointsNeeded} points minimum`);
      return;
    }

    setRedeemLoading(true);
    try {
      const response = await axios.post(`${API}/referral/redeem`,
        { points: pointsNeeded, reward_type: rewardType },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(response.data.message);
      fetchReferralInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'échange");
    } finally {
      setRedeemLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="referral-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <Gift className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Programme de <span className="gold-text">Parrainage</span>
          </h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Invitez vos amis et gagnez des récompenses exclusives : points, réductions et mois gratuits !
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Referral Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* Your Referral Code */}
            <div className="premium-card p-8">
              <h2 className="font-serif text-2xl mb-6 flex items-center gap-2">
                <Share2 className="w-6 h-6 text-[#D4AF37]" />
                Votre Code de Parrainage
              </h2>
              
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 bg-black/50 border border-[#D4AF37] p-4 text-center">
                  <span className="font-mono text-2xl gold-text tracking-widest" data-testid="referral-code">
                    {referralInfo?.referral_code}
                  </span>
                </div>
                <button
                  onClick={copyReferralCode}
                  className="btn-gold py-4 px-6"
                  data-testid="copy-referral-code"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>

              <button
                onClick={copyReferralLink}
                className="btn-outline w-full flex items-center justify-center gap-2"
                data-testid="copy-referral-link"
              >
                <Share2 className="w-4 h-4" />
                Copier le lien de parrainage
              </button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatBox 
                icon={<Users className="w-5 h-5" />} 
                value={referralInfo?.referral_count || 0} 
                label="Filleuls" 
              />
              <StatBox 
                icon={<Star className="w-5 h-5" />} 
                value={referralInfo?.referral_points || 0} 
                label="Points" 
              />
              <StatBox 
                icon={<Percent className="w-5 h-5" />} 
                value={`${referralInfo?.referral_discount || 0}%`} 
                label="Réduction" 
              />
              <StatBox 
                icon={<Calendar className="w-5 h-5" />} 
                value={referralInfo?.free_months_earned || 0} 
                label="Mois gratuits" 
              />
            </div>

            {/* Badge Section */}
            <div className="bg-[#0F0F0F] border border-white/5 p-6">
              <h3 className="font-serif text-xl mb-6 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#D4AF37]" />
                Vos Badges
              </h3>

              {/* Current Badge */}
              {referralInfo?.badge?.current ? (
                <div className="mb-6">
                  <div 
                    className="p-6 border-2 text-center"
                    style={{ borderColor: referralInfo.badge.current.color }}
                  >
                    <div 
                      className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
                      style={{ background: `linear-gradient(135deg, ${referralInfo.badge.current.color}40, ${referralInfo.badge.current.color}20)` }}
                    >
                      <Trophy className="w-10 h-10" style={{ color: referralInfo.badge.current.color }} />
                    </div>
                    <h4 className="font-serif text-2xl mb-2" style={{ color: referralInfo.badge.current.color }}>
                      Badge {referralInfo.badge.current.name}
                    </h4>
                    <p className="text-sm text-[#A1A1AA] mb-4">
                      +{referralInfo.badge.current.bonus_points} points bonus débloqués !
                    </p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {referralInfo.badge.current.perks?.map((perk, idx) => (
                        <span key={idx} className="text-xs bg-white/10 px-3 py-1">
                          {perk}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-6 border border-white/10 text-center">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                    <Trophy className="w-10 h-10 text-[#A1A1AA]" />
                  </div>
                  <h4 className="font-serif text-xl mb-2 text-[#A1A1AA]">Aucun badge</h4>
                  <p className="text-sm text-[#A1A1AA]">
                    Parrainez votre premier ami pour débloquer le badge Bronze !
                  </p>
                </div>
              )}

              {/* Next Badge Progress */}
              {referralInfo?.badge?.next && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-[#A1A1AA]">Prochain badge:</span>
                    <span className="text-sm font-medium" style={{ color: referralInfo.badge.next.color }}>
                      {referralInfo.badge.next.name}
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 overflow-hidden">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${Math.min(100, ((referralInfo?.referral_count || 0) / referralInfo.badge.next.min_referrals) * 100)}%`,
                        backgroundColor: referralInfo.badge.next.color
                      }}
                    />
                  </div>
                  <p className="text-xs text-[#A1A1AA] mt-2">
                    {referralInfo.badge.next.remaining || referralInfo.badge.next.min_referrals - (referralInfo?.referral_count || 0)} filleul(s) restant(s)
                  </p>
                </div>
              )}

              {/* All Badges Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {referralInfo?.all_badges && Object.entries(referralInfo.all_badges).map(([id, badge]) => {
                  const isUnlocked = (referralInfo?.referral_count || 0) >= badge.min_referrals;
                  return (
                    <div 
                      key={id}
                      className={`p-4 text-center border ${isUnlocked ? 'border-opacity-100' : 'border-white/10 opacity-50'}`}
                      style={{ borderColor: isUnlocked ? badge.color : undefined }}
                      data-testid={`badge-${id}`}
                    >
                      <div 
                        className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${isUnlocked ? '' : 'bg-white/5'}`}
                        style={isUnlocked ? { background: `linear-gradient(135deg, ${badge.color}40, ${badge.color}20)` } : {}}
                      >
                        <Trophy className="w-6 h-6" style={{ color: isUnlocked ? badge.color : '#A1A1AA' }} />
                      </div>
                      <h5 className="text-sm font-medium" style={{ color: isUnlocked ? badge.color : '#A1A1AA' }}>
                        {badge.name}
                      </h5>
                      <p className="text-xs text-[#A1A1AA] mt-1">
                        {badge.min_referrals} filleuls
                      </p>
                      {isUnlocked && (
                        <Check className="w-4 h-4 mx-auto mt-2 text-green-500" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rewards Section */}
            <div className="bg-[#0F0F0F] border border-white/5 p-6">
              <h3 className="font-serif text-xl mb-6 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#D4AF37]" />
                Échanger vos Points
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-white/10 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium">Réduction Vidéo</span>
                    <span className="text-[#D4AF37] font-mono text-sm">
                      {referralInfo?.rewards_config?.video_discount_points || 50} pts
                    </span>
                  </div>
                  <p className="text-sm text-[#A1A1AA] mb-4">
                    -{referralInfo?.rewards_config?.video_discount_percent || 20}% sur l'achat de vidéos
                  </p>
                  <button
                    onClick={() => handleRedeemPoints('video_discount')}
                    disabled={redeemLoading || (referralInfo?.referral_points || 0) < (referralInfo?.rewards_config?.video_discount_points || 50)}
                    className="btn-outline w-full text-sm flex items-center justify-center gap-2"
                    data-testid="redeem-video-discount"
                  >
                    {redeemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                    Échanger
                  </button>
                </div>

                <div className="border border-[#D4AF37]/30 p-4 bg-[#D4AF37]/5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium">Réduction Abonnement</span>
                    <span className="text-[#D4AF37]">Automatique</span>
                  </div>
                  <p className="text-sm text-[#A1A1AA] mb-4">
                    Jusqu'à {referralInfo?.rewards_config?.max_discount_percent || 30}% de réduction automatique
                  </p>
                  <div className="text-sm text-[#D4AF37]">
                    Actuel: {referralInfo?.referral_discount || 0}% de réduction
                  </div>
                </div>
              </div>
            </div>

            {/* Apply Code Section */}
            {!user?.referred_by && (
              <div className="bg-[#0F0F0F] border border-white/5 p-6">
                <h3 className="font-serif text-xl mb-4">Utiliser un code de parrainage</h3>
                <form onSubmit={handleApplyCode} className="flex gap-4">
                  <input
                    type="text"
                    value={applyCode}
                    onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                    placeholder="ELIXIR-XXXXXXXX"
                    className="input-dark flex-1 font-mono uppercase tracking-wider"
                    data-testid="apply-referral-input"
                  />
                  <button
                    type="submit"
                    disabled={applyLoading}
                    className="btn-gold flex items-center gap-2"
                    data-testid="apply-referral-btn"
                  >
                    {applyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Appliquer
                  </button>
                </form>
              </div>
            )}

            {/* Referred Users */}
            {referralInfo?.referred_users?.length > 0 && (
              <div className="bg-[#0F0F0F] border border-white/5 p-6">
                <h3 className="font-serif text-xl mb-4">Vos Filleuls</h3>
                <div className="space-y-3">
                  {referralInfo.referred_users.map((referred, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                          <Users className="w-4 h-4 text-[#D4AF37]" />
                        </div>
                        <span>{referred.full_name}</span>
                      </div>
                      {referred.subscription_tier && (
                        <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1">
                          {referred.subscription_tier}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* How it works */}
            <div className="bg-[#0F0F0F] border border-white/5 p-6">
              <h3 className="font-serif text-xl mb-6">Comment ça marche ?</h3>
              <div className="space-y-4">
                <Step number={1} title="Partagez votre code" description="Envoyez votre code unique à vos amis" />
                <Step number={2} title="Ils s'inscrivent" description="Vos amis utilisent votre code à l'inscription" />
                <Step number={3} title="Gagnez des récompenses" description={`+${referralInfo?.rewards_config?.points_per_referral || 100} points par filleul`} />
                <Step number={4} title="Bonus abonnement" description={`+${referralInfo?.rewards_config?.points_per_subscription || 50} points quand ils s'abonnent`} />
              </div>
            </div>

            {/* Leaderboard */}
            <div className="bg-[#0F0F0F] border border-white/5 p-6">
              <h3 className="font-serif text-xl mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-[#D4AF37]" />
                Top Parrains
              </h3>
              <div className="space-y-3">
                {leaderboard.slice(0, 5).map((user, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 flex items-center justify-center text-sm font-bold ${
                        idx === 0 ? 'text-[#D4AF37]' : idx === 1 ? 'text-gray-300' : idx === 2 ? 'text-amber-600' : 'text-[#A1A1AA]'
                      }`}>
                        {idx + 1}
                      </span>
                      <span className="text-sm">{user.full_name}</span>
                    </div>
                    <span className="text-sm text-[#D4AF37]">{user.referral_count} filleuls</span>
                  </div>
                ))}
                {leaderboard.length === 0 && (
                  <p className="text-sm text-[#A1A1AA] text-center py-4">
                    Soyez le premier parrain !
                  </p>
                )}
              </div>
            </div>

            {/* CTA */}
            <div className="premium-card p-6 text-center">
              <h4 className="font-serif text-lg mb-2">Pas encore abonné ?</h4>
              <p className="text-sm text-[#A1A1AA] mb-4">
                Profitez de votre réduction de {referralInfo?.referral_discount || 0}% !
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="btn-gold w-full"
                data-testid="referral-subscribe-cta"
              >
                Voir les abonnements
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const StatBox = ({ icon, value, label }) => (
  <div className="bg-[#0F0F0F] border border-white/5 p-4 text-center">
    <div className="text-[#D4AF37] mb-2 flex justify-center">{icon}</div>
    <div className="text-2xl font-bold gold-text">{value}</div>
    <div className="text-xs text-[#A1A1AA]">{label}</div>
  </div>
);

const Step = ({ number, title, description }) => (
  <div className="flex gap-3">
    <div className="w-8 h-8 bg-[#D4AF37] text-black font-bold flex items-center justify-center text-sm flex-shrink-0">
      {number}
    </div>
    <div>
      <h4 className="font-medium text-sm">{title}</h4>
      <p className="text-xs text-[#A1A1AA]">{description}</p>
    </div>
  </div>
);

// Notifications Component
const NotificationsDropdown = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (token) {
      fetchNotifications();
      // Poll every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error(error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'badge': return <Trophy className="w-4 h-4 text-[#D4AF37]" />;
      case 'referral': return <Users className="w-4 h-4 text-green-500" />;
      case 'subscription': return <CreditCard className="w-4 h-4 text-blue-500" />;
      default: return <Bell className="w-4 h-4 text-[#A1A1AA]" />;
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 hover:bg-white/5 rounded-lg transition-colors"
        data-testid="notifications-toggle"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#D4AF37] text-black text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-80 bg-[#0F0F0F] border border-white/10 shadow-xl z-50" data-testid="notifications-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="font-medium">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-[#D4AF37] hover:underline"
              >
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-[#A1A1AA]">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune notification</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className={`p-4 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                    !notif.is_read ? 'bg-[#D4AF37]/5' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className={`text-sm font-medium ${!notif.is_read ? 'text-white' : 'text-[#A1A1AA]'}`}>
                        {notif.title}
                      </h4>
                      <p className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">
                        {notif.message}
                      </p>
                      <span className="text-xs text-[#A1A1AA]/60 mt-2 block">
                        {new Date(notif.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    {!notif.is_read && (
                      <div className="w-2 h-2 bg-[#D4AF37] rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Become Creator Page
const BecomeCreatorPage = () => {
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    city: '',
    country: 'France',
    experience: 'none',
    content_types: [],
    social_media: '',
    portfolio_url: '',
    motivation: '',
    availability: 'part-time',
    has_equipment: false,
    accepts_terms: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchCreatorInfo();
  }, []);

  const fetchCreatorInfo = async () => {
    try {
      const response = await axios.get(`${API}/creators/info`);
      setCreatorInfo(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      content_types: prev.content_types.includes(type)
        ? prev.content_types.filter(t => t !== type)
        : [...prev.content_types, type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.content_types.length === 0) {
      toast.error("Veuillez sélectionner au moins un type de contenu");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/creators/apply`, formData);
      toast.success("Candidature envoyée avec succès !");
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" data-testid="creator-submitted">
        <div className="max-w-md w-full text-center animate-fade-in-up">
          <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-black" />
          </div>
          <h1 className="font-serif text-3xl mb-4">Candidature envoyée !</h1>
          <p className="text-[#A1A1AA] mb-8">
            Merci pour votre intérêt ! Notre équipe examinera votre candidature dans les 48-72h.
            Vous recevrez un email à l'adresse indiquée.
          </p>
          <Link to="/" className="btn-gold inline-block">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  const getBenefitIcon = (icon) => {
    const icons = {
      money: <DollarSign className="w-6 h-6" />,
      users: <Users className="w-6 h-6" />,
      shield: <Shield className="w-6 h-6" />,
      trending: <TrendingUp className="w-6 h-6" />,
      calendar: <Calendar className="w-6 h-6" />,
      support: <Headphones className="w-6 h-6" />
    };
    return icons[icon] || <Star className="w-6 h-6" />;
  };

  const getContentIcon = (id) => {
    const icons = {
      photo: <Camera className="w-5 h-5" />,
      video: <Video className="w-5 h-5" />,
      live: <Radio className="w-5 h-5" />,
      custom: <Sparkles className="w-5 h-5" />
    };
    return icons[id] || <Star className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="become-creator-page">
      {/* Hero Section */}
      <section className="px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto animate-fade-in-up">
          <Crown className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-6xl mb-6">
            Devenez <span className="gold-text">Créateur</span>
          </h1>
          <p className="text-xl text-[#A1A1AA] mb-8 max-w-2xl mx-auto">
            Rejoignez notre communauté exclusive de créateurs et transformez votre passion en revenus.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-gold text-lg px-8 py-4 animate-pulse-gold"
            data-testid="start-application-btn"
          >
            Postuler Maintenant
          </button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 py-16 bg-[#0F0F0F]">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-3xl text-center mb-12">
            Pourquoi nous <span className="gold-text">rejoindre</span> ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {creatorInfo?.benefits?.map((benefit, idx) => (
              <div key={idx} className="p-6 border border-white/5 hover:border-[#D4AF37]/30 transition-colors">
                <div className="text-[#D4AF37] mb-4">{getBenefitIcon(benefit.icon)}</div>
                <h3 className="font-serif text-xl mb-2">{benefit.title}</h3>
                <p className="text-sm text-[#A1A1AA]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-3xl text-center mb-12">
            Comment <span className="gold-text">ça marche</span> ?
          </h2>
          <div className="space-y-8">
            {creatorInfo?.process_steps?.map((step, idx) => (
              <div key={idx} className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-[#D4AF37] text-black font-bold text-xl flex items-center justify-center flex-shrink-0">
                  {step.step}
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="font-serif text-xl mb-2">{step.title}</h3>
                  <p className="text-[#A1A1AA]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="px-4 py-16 bg-[#0F0F0F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl mb-8">
            <span className="gold-text">Conditions</span> requises
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            {creatorInfo?.requirements?.map((req, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white/5 px-4 py-2">
                <Check className="w-4 h-4 text-[#D4AF37]" />
                <span className="text-sm">{req}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-16 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl mb-6">
            Prêt(e) à <span className="gold-text">commencer</span> ?
          </h2>
          <p className="text-[#A1A1AA] mb-8">
            Remplissez le formulaire de candidature et notre équipe vous contactera sous 48-72h.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-gold"
            data-testid="cta-apply-btn"
          >
            Remplir ma candidature
          </button>
        </div>
      </section>

      {/* Application Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/90 overflow-y-auto" data-testid="creator-application-modal">
          <div className="bg-[#0F0F0F] border border-white/10 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#0F0F0F]">
              <h2 className="font-serif text-2xl">Formulaire de Candidature</h2>
              <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Personal Info */}
              <div>
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  Informations personnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2">Nom complet *</label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="input-dark w-full"
                      required
                      data-testid="creator-fullname"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-dark w-full"
                      required
                      data-testid="creator-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Téléphone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-dark w-full"
                      data-testid="creator-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Date de naissance *</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={handleChange}
                      className="input-dark w-full"
                      required
                      data-testid="creator-dob"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Ville *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input-dark w-full"
                      required
                      data-testid="creator-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Pays *</label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="input-dark w-full"
                      data-testid="creator-country"
                    >
                      <option value="France">France</option>
                      <option value="Belgique">Belgique</option>
                      <option value="Suisse">Suisse</option>
                      <option value="Canada">Canada</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div>
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#D4AF37]" />
                  Expérience
                </h3>
                <div>
                  <label className="block text-sm mb-2">Niveau d'expérience *</label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="input-dark w-full"
                    data-testid="creator-experience"
                  >
                    <option value="none">Aucune expérience</option>
                    <option value="amateur">Amateur</option>
                    <option value="semi-pro">Semi-professionnel</option>
                    <option value="professional">Professionnel</option>
                  </select>
                </div>
              </div>

              {/* Content Types */}
              <div>
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-[#D4AF37]" />
                  Types de contenu *
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {creatorInfo?.content_types?.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleContentTypeToggle(type.id)}
                      className={`p-4 border text-center transition-colors ${
                        formData.content_types.includes(type.id)
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid={`content-type-${type.id}`}
                    >
                      <div className={`mx-auto mb-2 ${formData.content_types.includes(type.id) ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'}`}>
                        {getContentIcon(type.id)}
                      </div>
                      <span className="text-sm">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Social & Portfolio */}
              <div>
                <h3 className="font-serif text-lg mb-4 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#D4AF37]" />
                  Réseaux & Portfolio
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2">Réseaux sociaux</label>
                    <input
                      type="text"
                      name="social_media"
                      value={formData.social_media}
                      onChange={handleChange}
                      placeholder="@votre_compte"
                      className="input-dark w-full"
                      data-testid="creator-social"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2">Portfolio / Site web</label>
                    <input
                      type="url"
                      name="portfolio_url"
                      value={formData.portfolio_url}
                      onChange={handleChange}
                      placeholder="https://"
                      className="input-dark w-full"
                      data-testid="creator-portfolio"
                    />
                  </div>
                </div>
              </div>

              {/* Motivation */}
              <div>
                <label className="block text-sm mb-2">Motivation *</label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  placeholder="Pourquoi souhaitez-vous rejoindre Élixir Noir ?"
                  className="input-dark w-full h-24 resize-none"
                  required
                  data-testid="creator-motivation"
                />
              </div>

              {/* Availability & Equipment */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-2">Disponibilité *</label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    className="input-dark w-full"
                    data-testid="creator-availability"
                  >
                    <option value="full-time">Temps plein</option>
                    <option value="part-time">Temps partiel</option>
                    <option value="occasional">Occasionnel</option>
                  </select>
                </div>
                <div className="flex items-center gap-3 pt-8">
                  <input
                    type="checkbox"
                    name="has_equipment"
                    checked={formData.has_equipment}
                    onChange={handleChange}
                    id="has_equipment"
                    className="w-5 h-5"
                    data-testid="creator-equipment"
                  />
                  <label htmlFor="has_equipment" className="text-sm">
                    Je possède du matériel (caméra, éclairage...)
                  </label>
                </div>
              </div>

              {/* Terms */}
              <div className="flex items-start gap-3 p-4 bg-white/5 border border-white/10">
                <input
                  type="checkbox"
                  name="accepts_terms"
                  checked={formData.accepts_terms}
                  onChange={handleChange}
                  id="accepts_terms"
                  className="w-5 h-5 mt-0.5"
                  required
                  data-testid="creator-terms"
                />
                <label htmlFor="accepts_terms" className="text-sm text-[#A1A1AA]">
                  Je certifie avoir 18 ans ou plus, et j'accepte les conditions d'utilisation
                  et la politique de confidentialité d'Élixir Noir. *
                </label>
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-outline flex-1"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-gold flex-1 flex items-center justify-center gap-2"
                  data-testid="creator-submit"
                >
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  Envoyer ma candidature
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

// Protected Media Component - Prevents downloading/copying
const ProtectedMedia = ({ src, type = "image", alt = "", className = "" }) => {
  const handleContextMenu = (e) => {
    e.preventDefault();
    toast.error("Le téléchargement de ce contenu est interdit");
  };

  const handleDragStart = (e) => {
    e.preventDefault();
  };

  if (type === "video") {
    return (
      <div className="protected-media relative" onContextMenu={handleContextMenu}>
        <video
          src={src}
          className={`${className} pointer-events-auto`}
          controls
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onContextMenu={handleContextMenu}
          onDragStart={handleDragStart}
          style={{ userSelect: 'none' }}
        />
        <div className="absolute inset-0 pointer-events-none" />
      </div>
    );
  }

  return (
    <div className="protected-media relative" onContextMenu={handleContextMenu}>
      <img
        src={src}
        alt={alt}
        className={`${className} pointer-events-none select-none`}
        draggable={false}
        onDragStart={handleDragStart}
        style={{ userSelect: 'none', WebkitUserDrag: 'none' }}
      />
      {/* Invisible overlay to prevent interactions */}
      <div className="absolute inset-0" />
    </div>
  );
};

// Messages Page
const MessagesPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const { user, token } = useAuth();
  const messagesEndRef = React.useRef(null);

  useEffect(() => {
    fetchConversations();
    // Poll for new messages
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    if (selectedConv) {
      fetchMessages(selectedConv);
    }
  }, [selectedConv]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (convId) => {
    try {
      const response = await axios.get(`${API}/messages/conversations/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
      setOtherUser(response.data.other_user);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv) return;

    setSendingMessage(true);
    try {
      // Find recipient from conversation
      const conv = conversations.find(c => c.id === selectedConv);
      const recipientId = conv.participant_1 === user.id ? conv.participant_2 : conv.participant_1;

      await axios.post(`${API}/messages/send`, {
        recipient_id: recipientId,
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage('');
      fetchMessages(selectedConv);
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" data-testid="messages-page">
      <div className="h-[calc(100vh-64px)] flex">
        {/* Conversations List */}
        <div className={`w-full md:w-80 bg-[#0F0F0F] border-r border-white/5 flex flex-col ${selectedConv ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-4 border-b border-white/5">
            <h1 className="font-serif text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
              Messages
            </h1>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-[#A1A1AA]">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune conversation</p>
                <p className="text-xs mt-2">Commencez une conversation depuis le profil d'un créateur</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setSelectedConv(conv.id)}
                  className={`w-full p-4 text-left border-b border-white/5 hover:bg-white/5 transition-colors ${
                    selectedConv === conv.id ? 'bg-[#D4AF37]/10 border-l-2 border-l-[#D4AF37]' : ''
                  }`}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium truncate">{conv.other_user?.full_name || 'Utilisateur'}</h3>
                        {conv.unread_count > 0 && (
                          <span className="w-5 h-5 bg-[#D4AF37] text-black text-xs font-bold rounded-full flex items-center justify-center">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#A1A1AA] truncate">{formatDate(conv.last_message_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className={`flex-1 flex flex-col ${!selectedConv ? 'hidden md:flex' : 'flex'}`}>
          {selectedConv ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-white/5 bg-[#0F0F0F] flex items-center gap-4">
                <button
                  onClick={() => setSelectedConv(null)}
                  className="md:hidden p-2 hover:bg-white/5 rounded-lg"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h2 className="font-medium">{otherUser?.full_name || 'Chargement...'}</h2>
                  {otherUser?.subscription_tier && (
                    <span className="text-xs text-[#D4AF37]">{otherUser.subscription_tier}</span>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg) => {
                  const isOwn = msg.sender_id === user.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isOwn ? 'order-1' : 'order-2'}`}>
                        {/* Media content */}
                        {msg.media_url && (
                          <div className="mb-2">
                            {msg.is_paid && !msg.is_unlocked ? (
                              <div className="bg-black/50 border border-white/10 p-8 text-center">
                                <Lock className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                                <p className="text-sm text-[#A1A1AA] mb-2">Contenu payant</p>
                                <button className="btn-gold text-sm py-2 px-4">
                                  Débloquer - {msg.price}€
                                </button>
                              </div>
                            ) : (
                              <ProtectedMedia
                                src={msg.media_url}
                                type={msg.media_type}
                                className="max-w-full rounded-lg"
                              />
                            )}
                          </div>
                        )}
                        
                        {/* Text content */}
                        {msg.content && (
                          <div className={`p-3 ${
                            isOwn 
                              ? 'bg-[#D4AF37] text-black' 
                              : 'bg-[#1A1A1A] border border-white/10'
                          }`}>
                            <p className="text-sm">{msg.content}</p>
                          </div>
                        )}
                        <p className={`text-xs text-[#A1A1AA] mt-1 ${isOwn ? 'text-right' : 'text-left'}`}>
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-white/5 bg-[#0F0F0F]">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="input-dark flex-1"
                    data-testid="message-input"
                  />
                  <button
                    type="submit"
                    disabled={sendingMessage || !newMessage.trim()}
                    className="btn-gold py-3 px-4"
                    data-testid="send-message-btn"
                  >
                    {sendingMessage ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-[#A1A1AA] mt-2 flex items-center gap-1">
                  <Shield className="w-3 h-3" />
                  Messages chiffrés et sécurisés
                </p>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageSquare className="w-16 h-16 text-[#A1A1AA] mx-auto mb-4 opacity-50" />
                <h2 className="font-serif text-xl mb-2">Sélectionnez une conversation</h2>
                <p className="text-sm text-[#A1A1AA]">
                  Choisissez une conversation dans la liste pour voir les messages
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Security Watermark */}
      <div className="fixed bottom-4 right-4 text-xs text-white/10 pointer-events-none select-none">
        Élixir Noir © Contenu Protégé
      </div>
      <Footer />
    </div>
  );
};

// Admin Dashboard Page
const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [content, setContent] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error("Accès administrateur requis");
      navigate('/dashboard');
      return;
    }
    fetchStats();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'applications') fetchApplications();
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'content') fetchContent();
    if (activeTab === 'transactions') fetchTransactions();
  }, [activeTab]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (status = null) => {
    try {
      const params = status ? { status } : {};
      const response = await axios.get(`${API}/admin/creator-applications`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setApplications(response.data.applications);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchContent = async (contentType = 'articles') => {
    try {
      const response = await axios.get(`${API}/admin/content`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { content_type: contentType }
      });
      setContent(response.data.items);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions);
    } catch (error) {
      console.error(error);
    }
  };

  const handleReviewApplication = async (appId, status, notes = null) => {
    try {
      await axios.put(`${API}/admin/creator-applications/${appId}/review`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status, notes }
      });
      toast.success(`Candidature ${status === 'accepted' ? 'acceptée' : status === 'rejected' ? 'refusée' : 'mise à jour'}`);
      fetchApplications();
      setSelectedApp(null);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleUpdateUserRole = async (userId, role) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { role }
      });
      toast.success("Rôle mis à jour");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleBanUser = async (userId, banned) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/ban`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { banned }
      });
      toast.success(banned ? "Utilisateur banni" : "Utilisateur débanni");
      fetchUsers();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteContent = async (contentType, contentId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce contenu ?")) return;
    
    try {
      await axios.delete(`${API}/admin/content/${contentType}/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Contenu supprimé");
      fetchContent(contentType);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'applications', label: 'Candidatures', icon: <UserCheck className="w-4 h-4" />, badge: stats?.creator_applications?.pending },
    { id: 'users', label: 'Utilisateurs', icon: <Users className="w-4 h-4" /> },
    { id: 'content', label: 'Contenu', icon: <FileText className="w-4 h-4" /> },
    { id: 'transactions', label: 'Transactions', icon: <CreditCard className="w-4 h-4" /> }
  ];

  return (
    <div className="min-h-screen pt-16 bg-[#050505]" data-testid="admin-page">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-[calc(100vh-64px)] bg-[#0F0F0F] border-r border-white/5 p-4 hidden md:block">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-[#D4AF37] mb-2">
              <Shield className="w-5 h-5" />
              <span className="font-serif text-lg">Administration</span>
            </div>
            <p className="text-xs text-[#A1A1AA]">Panel de gestion</p>
          </div>

          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-l-2 border-[#D4AF37]'
                    : 'text-[#A1A1AA] hover:bg-white/5'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <div className="flex items-center gap-3">
                  {tab.icon}
                  {tab.label}
                </div>
                {tab.badge > 0 && (
                  <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Mobile Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto md:hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#D4AF37] text-black'
                    : 'bg-white/5 text-[#A1A1AA]'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fade-in-up">
              <h1 className="font-serif text-2xl">Tableau de bord</h1>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <AdminStatCard
                  icon={<Users className="w-6 h-6" />}
                  label="Utilisateurs"
                  value={stats?.users?.total || 0}
                  subtext={`+${stats?.users?.recent_signups || 0} cette semaine`}
                />
                <AdminStatCard
                  icon={<CreditCard className="w-6 h-6" />}
                  label="Abonnés actifs"
                  value={stats?.subscriptions?.total_active || 0}
                  subtext="Tous niveaux"
                />
                <AdminStatCard
                  icon={<DollarSign className="w-6 h-6" />}
                  label="Revenus"
                  value={`${stats?.revenue?.total || 0}€`}
                  subtext="Total"
                />
                <AdminStatCard
                  icon={<UserCheck className="w-6 h-6" />}
                  label="Candidatures"
                  value={stats?.creator_applications?.pending || 0}
                  subtext="En attente"
                  highlight={stats?.creator_applications?.pending > 0}
                />
              </div>

              {/* Subscriptions Breakdown */}
              <div className="bg-[#0F0F0F] border border-white/5 p-6">
                <h3 className="font-serif text-lg mb-4">Répartition des abonnements</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-400">{stats?.subscriptions?.basic || 0}</div>
                    <div className="text-xs text-[#A1A1AA]">Basic</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">{stats?.subscriptions?.premium || 0}</div>
                    <div className="text-xs text-[#A1A1AA]">Premium</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#D4AF37]">{stats?.subscriptions?.vip || 0}</div>
                    <div className="text-xs text-[#A1A1AA]">VIP</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-400">{stats?.subscriptions?.lifetime || 0}</div>
                    <div className="text-xs text-[#A1A1AA]">Lifetime</div>
                  </div>
                </div>
              </div>

              {/* Content & Activity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-[#0F0F0F] border border-white/5 p-6">
                  <h3 className="font-serif text-lg mb-4">Contenu</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Articles</span>
                      <span>{stats?.content?.articles || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Vidéos</span>
                      <span>{stats?.content?.videos || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Posts Forum</span>
                      <span>{stats?.content?.forum_posts || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-[#0F0F0F] border border-white/5 p-6">
                  <h3 className="font-serif text-lg mb-4">Messagerie</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Conversations</span>
                      <span>{stats?.messaging?.conversations || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Messages</span>
                      <span>{stats?.messaging?.messages || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Utilisateurs vérifiés</span>
                      <span>{stats?.users?.verified || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center justify-between">
                <h1 className="font-serif text-2xl">Candidatures Créateurs</h1>
                <div className="flex gap-2">
                  <button
                    onClick={() => fetchApplications('pending')}
                    className="btn-outline text-sm py-2 px-4"
                  >
                    En attente ({stats?.creator_applications?.pending || 0})
                  </button>
                  <button
                    onClick={() => fetchApplications()}
                    className="btn-outline text-sm py-2 px-4"
                  >
                    Toutes
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {applications.map((app) => (
                  <div key={app.id} className="bg-[#0F0F0F] border border-white/5 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-lg">{app.full_name}</h3>
                        <p className="text-sm text-[#A1A1AA]">{app.email}</p>
                        <div className="flex gap-4 mt-2 text-xs text-[#A1A1AA]">
                          <span>{app.city}, {app.country}</span>
                          <span>Expérience: {app.experience}</span>
                          <span>Dispo: {app.availability}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          {app.content_types?.map((type) => (
                            <span key={type} className="text-xs bg-white/10 px-2 py-1">{type}</span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-3 py-1 ${
                          app.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                          app.status === 'reviewing' ? 'bg-blue-500/20 text-blue-500' :
                          app.status === 'accepted' ? 'bg-green-500/20 text-green-500' :
                          'bg-red-500/20 text-red-500'
                        }`}>
                          {app.status}
                        </span>
                        <p className="text-xs text-[#A1A1AA] mt-2">
                          {new Date(app.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 p-4 bg-black/30">
                      <p className="text-sm text-[#A1A1AA]">
                        <strong>Motivation:</strong> {app.motivation}
                      </p>
                    </div>

                    {app.status === 'pending' && (
                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => handleReviewApplication(app.id, 'accepted')}
                          className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                          data-testid={`accept-app-${app.id}`}
                        >
                          <Check className="w-4 h-4" /> Accepter
                        </button>
                        <button
                          onClick={() => handleReviewApplication(app.id, 'reviewing')}
                          className="btn-outline text-sm py-2 px-4"
                        >
                          En revue
                        </button>
                        <button
                          onClick={() => handleReviewApplication(app.id, 'rejected')}
                          className="btn-outline text-sm py-2 px-4 text-red-500 border-red-500/30"
                        >
                          <X className="w-4 h-4" /> Refuser
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {applications.length === 0 && (
                  <div className="text-center py-12 text-[#A1A1AA]">
                    <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune candidature</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center justify-between">
                <h1 className="font-serif text-2xl">Utilisateurs</h1>
                <div className="flex gap-2">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && fetchUsers()}
                      placeholder="Rechercher..."
                      className="input-dark pl-10 py-2 w-64"
                      data-testid="admin-user-search"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-[#0F0F0F] border border-white/5 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Utilisateur</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Rôle</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Abonnement</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Vérifié</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Inscription</th>
                      <th className="text-right p-4 text-xs text-[#A1A1AA] font-normal">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                        <td className="p-4">
                          <div>
                            <p className="font-medium">{u.full_name}</p>
                            <p className="text-xs text-[#A1A1AA]">{u.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <select
                            value={u.role || 'user'}
                            onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                            className="input-dark text-xs py-1 px-2"
                          >
                            <option value="user">Utilisateur</option>
                            <option value="creator">Créateur</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 ${
                            u.subscription_tier === 'vip' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' :
                            u.subscription_tier === 'premium' ? 'bg-blue-500/20 text-blue-500' :
                            u.subscription_tier === 'basic' ? 'bg-gray-500/20 text-gray-400' :
                            u.subscription_tier === 'lifetime' ? 'bg-purple-500/20 text-purple-400' :
                            'bg-white/10 text-[#A1A1AA]'
                          }`}>
                            {u.subscription_tier || 'Aucun'}
                          </span>
                        </td>
                        <td className="p-4">
                          {u.is_id_verified ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <X className="w-4 h-4 text-red-500" />
                          )}
                        </td>
                        <td className="p-4 text-sm text-[#A1A1AA]">
                          {new Date(u.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleBanUser(u.id, !u.is_banned)}
                            className={`text-xs px-3 py-1 ${u.is_banned ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}
                          >
                            {u.is_banned ? 'Débannir' : 'Bannir'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Content Tab */}
          {activeTab === 'content' && (
            <div className="space-y-6 animate-fade-in-up">
              <div className="flex items-center justify-between">
                <h1 className="font-serif text-2xl">Modération du contenu</h1>
                <div className="flex gap-2">
                  <button onClick={() => fetchContent('articles')} className="btn-outline text-sm py-2 px-4">
                    Articles
                  </button>
                  <button onClick={() => fetchContent('videos')} className="btn-outline text-sm py-2 px-4">
                    Vidéos
                  </button>
                  <button onClick={() => fetchContent('forum_posts')} className="btn-outline text-sm py-2 px-4">
                    Forum
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {content.map((item) => (
                  <div key={item.id} className="bg-[#0F0F0F] border border-white/5 p-6 flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium">{item.title}</h3>
                      <p className="text-sm text-[#A1A1AA] mt-1 line-clamp-2">
                        {item.content || item.description || item.excerpt}
                      </p>
                      <div className="flex gap-4 mt-2 text-xs text-[#A1A1AA]">
                        <span>Par: {item.author_name}</span>
                        <span>{new Date(item.created_at).toLocaleDateString('fr-FR')}</span>
                        {item.is_premium && <span className="text-[#D4AF37]">Premium</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteContent('articles', item.id)}
                      className="p-2 text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}

                {content.length === 0 && (
                  <div className="text-center py-12 text-[#A1A1AA]">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Aucun contenu</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="space-y-6 animate-fade-in-up">
              <h1 className="font-serif text-2xl">Transactions</h1>

              <div className="bg-[#0F0F0F] border border-white/5 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-black/30">
                    <tr>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">ID</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Utilisateur</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Plan</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Montant</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Statut</th>
                      <th className="text-left p-4 text-xs text-[#A1A1AA] font-normal">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.id} className="border-t border-white/5">
                        <td className="p-4 text-xs font-mono text-[#A1A1AA]">{t.id.slice(0, 8)}...</td>
                        <td className="p-4">
                          <div>
                            <p className="text-sm">{t.user?.full_name || 'N/A'}</p>
                            <p className="text-xs text-[#A1A1AA]">{t.user?.email || ''}</p>
                          </div>
                        </td>
                        <td className="p-4 text-sm">{t.plan_id}</td>
                        <td className="p-4 text-sm font-medium">{t.amount}€</td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 ${
                            t.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' :
                            t.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                            'bg-red-500/20 text-red-500'
                          }`}>
                            {t.payment_status}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-[#A1A1AA]">
                          {new Date(t.created_at).toLocaleDateString('fr-FR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {transactions.length === 0 && (
                <div className="text-center py-12 text-[#A1A1AA]">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Aucune transaction</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

const AdminStatCard = ({ icon, label, value, subtext, highlight = false }) => (
  <div className={`p-6 ${highlight ? 'premium-card' : 'bg-[#0F0F0F] border border-white/5'}`}>
    <div className="text-[#D4AF37] mb-3">{icon}</div>
    <div className="text-2xl font-bold">{value}</div>
    <div className="text-sm text-[#A1A1AA]">{label}</div>
    <div className="text-xs text-[#A1A1AA]/60 mt-1">{subtext}</div>
  </div>
);

// Global Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5 mt-auto" data-testid="global-footer">
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand & About */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-8 h-8 text-[#D4AF37]" />
              <span className="font-serif text-xl gold-text font-bold">Élixir Noir</span>
            </div>
            <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">
              Plateforme premium exclusive dédiée aux connaisseurs. 
              Contenu de qualité supérieure dans un environnement sécurisé et privé.
            </p>
            <div className="flex gap-4">
              <a href="#" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-serif text-lg mb-4 text-white">Navigation</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Accueil</Link></li>
              <li><Link to="/articles" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Articles</Link></li>
              <li><Link to="/videos" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Vidéos</Link></li>
              <li><Link to="/forum" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Forum</Link></li>
              <li><Link to="/pricing" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Abonnements</Link></li>
              <li><Link to="/become-creator" className="text-sm text-[#D4AF37] hover:text-[#D4AF37]/80 transition-colors">Devenir Créateur</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-serif text-lg mb-4 text-white">Informations Légales</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Qui sommes-nous ?</Link></li>
              <li><Link to="/privacy" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Politique de Confidentialité</Link></li>
              <li><Link to="/terms" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Conditions Générales</Link></li>
              <li><Link to="/cookies" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Politique des Cookies</Link></li>
              <li><Link to="/legal" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] transition-colors">Mentions Légales</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg mb-4 text-white">Nous Contacter</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white">+34 900 123 456</p>
                  <p className="text-xs text-[#A1A1AA]">Lun-Ven 9h-18h</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white">contact@elixirnoir.com</p>
                  <p className="text-xs text-[#A1A1AA]">Contact général</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white">support@elixirnoir.com</p>
                  <p className="text-xs text-[#A1A1AA]">Support technique</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white">pro@elixirnoir.com</p>
                  <p className="text-xs text-[#A1A1AA]">Partenariats & Créateurs</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white">Passeig de Gràcia 100</p>
                  <p className="text-xs text-[#A1A1AA]">08008 Barcelona, España</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-[#A1A1AA]">
              © 2024 Élixir Noir. Tous droits réservés. Site réservé aux personnes majeures (18+).
            </p>
            <div className="flex items-center gap-6 text-xs text-[#A1A1AA]">
              <span className="flex items-center gap-1">
                <Shield className="w-3 h-3 text-[#D4AF37]" /> Paiement sécurisé
              </span>
              <span className="flex items-center gap-1">
                <Lock className="w-3 h-3 text-[#D4AF37]" /> Données protégées
              </span>
              <span className="flex items-center gap-1">
                <Check className="w-3 h-3 text-[#D4AF37]" /> RGPD Conforme
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// About Us Page
const AboutPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="about-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <Crown className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Qui <span className="gold-text">sommes-nous</span> ?
          </h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Découvrez l'histoire et les valeurs d'Élixir Noir
          </p>
        </div>

        <div className="space-y-12">
          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-2xl mb-4 gold-text">Notre Histoire</h2>
            <p className="text-[#A1A1AA] leading-relaxed mb-4">
              Fondé en 2024 par <span className="text-[#D4AF37] font-semibold">Monsieur TCHENG LAURENT</span>, Élixir Noir est né d'une vision simple : créer une plateforme premium 
              où qualité et exclusivité sont au cœur de chaque expérience. Notre équipe passionnée 
              travaille quotidiennement pour offrir à nos membres un espace unique et sécurisé.
            </p>
            <p className="text-[#A1A1AA] leading-relaxed">
              Nous croyons en la valeur du contenu authentique et en la protection de la vie privée 
              de nos utilisateurs. C'est pourquoi nous avons développé une infrastructure de pointe 
              garantissant la sécurité et la confidentialité de toutes les données.
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-2xl mb-4 gold-text">Nos Valeurs</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <Shield className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="font-medium mb-2">Sécurité</h3>
                <p className="text-sm text-[#A1A1AA]">Protection maximale des données et du contenu</p>
              </div>
              <div className="text-center">
                <Award className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="font-medium mb-2">Qualité</h3>
                <p className="text-sm text-[#A1A1AA]">Contenu premium sélectionné avec soin</p>
              </div>
              <div className="text-center">
                <Users className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h3 className="font-medium mb-2">Communauté</h3>
                <p className="text-sm text-[#A1A1AA]">Un espace d'échange respectueux et bienveillant</p>
              </div>
            </div>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-2xl mb-4 gold-text">Notre Équipe</h2>
            
            <div className="premium-card p-6 mb-6">
              <div className="flex items-start gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-[#D4AF37] to-[#FFD700] rounded-full flex items-center justify-center text-black text-2xl font-bold shrink-0">
                  LT
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">Monsieur TCHENG LAURENT</h3>
                  <p className="text-[#D4AF37] mb-3">Fondateur & Directeur Général</p>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    Visionnaire et entrepreneur passionné, Laurent TCHENG dirige Élixir Noir avec 
                    pour mission de créer une expérience premium inégalée. Son expertise en 
                    technologie et sa compréhension approfondie des attentes des membres font 
                    d'Élixir Noir une plateforme de référence.
                  </p>
                </div>
              </div>
            </div>
            
            <p className="text-[#A1A1AA] leading-relaxed">
              Basés à <span className="text-[#D4AF37] font-semibold">Barcelone</span>, nous sommes disponibles du lundi au vendredi pour répondre à toutes 
              vos questions et vous accompagner dans votre expérience Élixir Noir.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Privacy Policy Page
const PrivacyPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="privacy-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <Shield className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Politique de <span className="gold-text">Confidentialité</span>
          </h1>
          <p className="text-[#A1A1AA]">Dernière mise à jour : Janvier 2024</p>
        </div>

        <div className="space-y-8 text-[#A1A1AA]">
          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">1. Collecte des Données</h2>
            <p className="leading-relaxed mb-4">
              Nous collectons les informations que vous nous fournissez directement, notamment :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Informations d'inscription (nom, email, date de naissance)</li>
              <li>Documents de vérification d'identité</li>
              <li>Informations de paiement (traitées par Stripe)</li>
              <li>Contenus que vous publiez sur la plateforme</li>
              <li>Communications avec notre service client</li>
            </ul>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">2. Utilisation des Données</h2>
            <p className="leading-relaxed mb-4">
              Vos données sont utilisées pour :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Fournir et améliorer nos services</li>
              <li>Vérifier votre identité et votre âge</li>
              <li>Traiter les paiements et abonnements</li>
              <li>Vous envoyer des notifications importantes</li>
              <li>Assurer la sécurité de la plateforme</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">3. Protection des Données</h2>
            <p className="leading-relaxed">
              Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles 
              appropriées pour protéger vos données personnelles contre tout accès non autorisé, 
              modification, divulgation ou destruction. Toutes les données sont chiffrées en 
              transit et au repos.
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">4. Vos Droits (RGPD)</h2>
            <p className="leading-relaxed mb-4">
              Conformément au RGPD, vous disposez des droits suivants :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement (« droit à l'oubli »)</li>
              <li>Droit à la limitation du traitement</li>
              <li>Droit à la portabilité des données</li>
              <li>Droit d'opposition</li>
            </ul>
            <p className="mt-4">
              Pour exercer ces droits, contactez-nous à : <span className="text-[#D4AF37]">privacy@elixirnoir.com</span>
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">5. Contact</h2>
            <p className="leading-relaxed">
              Pour toute question concernant cette politique de confidentialité, contactez notre 
              Délégué à la Protection des Données :<br /><br />
              Email : <span className="text-[#D4AF37]">dpo@elixirnoir.com</span><br />
              Adresse : Passeig de Gràcia 100, 08008 Barcelona, España
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Terms of Service Page
const TermsPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="terms-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <FileText className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Conditions <span className="gold-text">Générales</span>
          </h1>
          <p className="text-[#A1A1AA]">Dernière mise à jour : Janvier 2024</p>
        </div>

        <div className="space-y-8 text-[#A1A1AA]">
          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">1. Accès au Service</h2>
            <p className="leading-relaxed mb-4">
              L'accès à Élixir Noir est strictement réservé aux personnes majeures (18 ans et plus). 
              En créant un compte, vous certifiez :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Avoir l'âge légal requis dans votre pays de résidence</li>
              <li>Fournir des informations exactes et véridiques</li>
              <li>Être responsable de la confidentialité de votre compte</li>
              <li>Accepter les présentes conditions générales</li>
            </ul>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">2. Abonnements et Paiements</h2>
            <p className="leading-relaxed mb-4">
              Les abonnements sont facturés selon la formule choisie (mensuel ou à vie). 
              Les paiements sont traités de manière sécurisée par Stripe.
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Les abonnements mensuels sont renouvelés automatiquement</li>
              <li>Vous pouvez annuler à tout moment depuis votre compte</li>
              <li>Aucun remboursement n'est effectué pour les périodes en cours</li>
              <li>L'accès à vie est un paiement unique sans renouvellement</li>
            </ul>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">3. Propriété Intellectuelle</h2>
            <p className="leading-relaxed">
              Tout le contenu disponible sur Élixir Noir (textes, images, vidéos, logos) est 
              protégé par le droit d'auteur. Toute reproduction, distribution ou utilisation 
              non autorisée est strictement interdite et passible de poursuites judiciaires. 
              Le téléchargement, la copie ou la capture d'écran du contenu sont interdits.
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">4. Comportement des Utilisateurs</h2>
            <p className="leading-relaxed mb-4">
              En utilisant notre plateforme, vous vous engagez à :
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Respecter les autres membres et créateurs</li>
              <li>Ne pas partager de contenu illégal ou offensant</li>
              <li>Ne pas tenter de contourner les mesures de sécurité</li>
              <li>Ne pas utiliser de logiciels automatisés ou bots</li>
              <li>Signaler tout comportement inapproprié</li>
            </ul>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">5. Résiliation</h2>
            <p className="leading-relaxed">
              Nous nous réservons le droit de suspendre ou résilier tout compte en cas de 
              violation des présentes conditions, sans préavis ni remboursement. Vous pouvez 
              également supprimer votre compte à tout moment depuis les paramètres.
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">6. Contact</h2>
            <p className="leading-relaxed">
              Pour toute question concernant ces conditions, contactez-nous :<br /><br />
              Email : <span className="text-[#D4AF37]">legal@elixirnoir.com</span><br />
              Téléphone : <span className="text-[#D4AF37]">+34 900 123 456</span>
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Legal Notice Page
const LegalPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="legal-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <FileText className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Mentions <span className="gold-text">Légales</span>
          </h1>
        </div>

        <div className="space-y-8 text-[#A1A1AA]">
          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Éditeur du Site</h2>
            <p className="leading-relaxed">
              <strong className="text-white">Élixir Noir SAS</strong><br />
              Capital social : 10 000 €<br />
              RCS Barcelona : 123 456 789<br />
              SIRET : 123 456 789 00012<br />
              TVA Intracommunautaire : FR 12 345678901<br /><br />
              Siège social :<br />
              Passeig de Gràcia 100<br />
              08008 Barcelona, España
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Directeur de la Publication</h2>
            <p className="leading-relaxed">
              <strong className="text-white">Monsieur TCHENG LAURENT</strong>, Président & Fondateur<br />
              Contact : <span className="text-[#D4AF37]">direction@elixirnoir.com</span>
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Hébergement</h2>
            <p className="leading-relaxed">
              Le site est hébergé par :<br /><br />
              <strong className="text-white">Emergent Labs</strong><br />
              Hébergement cloud sécurisé<br />
              Infrastructure conforme RGPD
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Contact</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-white font-medium mb-2">Service Client</h3>
                <p>Téléphone : +34 900 123 456</p>
                <p>Email : contact@elixirnoir.com</p>
                <p>Horaires : Lun-Ven 9h-18h</p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Service Juridique</h3>
                <p>Email : legal@elixirnoir.com</p>
                <p>Courrier : Voir adresse ci-dessus</p>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Cookies Policy Page
const CookiesPage = () => {
  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="cookies-page">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <Shield className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Politique des <span className="gold-text">Cookies</span>
          </h1>
        </div>

        <div className="space-y-8 text-[#A1A1AA]">
          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Qu'est-ce qu'un Cookie ?</h2>
            <p className="leading-relaxed">
              Un cookie est un petit fichier texte stocké sur votre appareil lors de votre 
              visite sur notre site. Les cookies nous permettent de vous offrir une expérience 
              personnalisée et de mémoriser vos préférences.
            </p>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Types de Cookies Utilisés</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-medium mb-2">Cookies Essentiels</h3>
                <p>Nécessaires au fonctionnement du site (authentification, sécurité).</p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Cookies de Performance</h3>
                <p>Nous aident à comprendre comment vous utilisez le site.</p>
              </div>
              <div>
                <h3 className="text-white font-medium mb-2">Cookies de Préférences</h3>
                <p>Mémorisent vos choix (langue, thème, vérification d'âge).</p>
              </div>
            </div>
          </section>

          <section className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-xl mb-4 text-white">Gestion des Cookies</h2>
            <p className="leading-relaxed">
              Vous pouvez à tout moment modifier vos préférences de cookies via les paramètres 
              de votre navigateur. Notez que la désactivation de certains cookies peut affecter 
              votre expérience sur le site.
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    try {
      // Send contact form to backend
      const response = await axios.post(`${API}/contact/send`, formData);
      
      setSending(false);
      setSent(true);
      toast.success("Message envoyé ! Nous vous répondrons sous 24-48h.");
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSent(false);
        setFormData({ name: '', email: '', subject: 'general', message: '' });
      }, 3000);
    } catch (error) {
      setSending(false);
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi du message");
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <Mail className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl mb-4">
            Nous <span className="gold-text">Contacter</span>
          </h1>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto">
            Une question ? Un problème ? Notre équipe est là pour vous aider.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-[#0F0F0F] border border-white/5 p-8">
            <h2 className="font-serif text-2xl mb-6">Envoyez-nous un message</h2>
            
            {sent ? (
              <div className="text-center py-12">
                <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="font-serif text-xl mb-2">Message envoyé !</h3>
                <p className="text-[#A1A1AA]">Nous vous répondrons dans les plus brefs délais.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm mb-2">Nom complet</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="input-dark w-full"
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="input-dark w-full"
                    required
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2">Sujet</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="input-dark w-full"
                    data-testid="contact-subject"
                  >
                    <option value="general">Question générale</option>
                    <option value="support">Support technique</option>
                    <option value="billing">Facturation</option>
                    <option value="partnership">Partenariat</option>
                    <option value="creator">Devenir créateur</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="input-dark w-full h-32 resize-none"
                    required
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="btn-gold w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Envoyer
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-[#0F0F0F] border border-white/5 p-8">
              <h2 className="font-serif text-2xl mb-6">Informations de Contact</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Téléphone</h3>
                    <p className="text-[#D4AF37] text-lg">+34 900 123 456</p>
                    <p className="text-sm text-[#A1A1AA]">Lun-Ven 9h-18h (heure de Barcelone)</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Emails</h3>
                    <p><span className="text-[#D4AF37]">contact@elixirnoir.com</span> <span className="text-xs text-[#A1A1AA]">- Général</span></p>
                    <p><span className="text-[#D4AF37]">support@elixirnoir.com</span> <span className="text-xs text-[#A1A1AA]">- Support</span></p>
                    <p><span className="text-[#D4AF37]">pro@elixirnoir.com</span> <span className="text-xs text-[#A1A1AA]">- Professionnel</span></p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Adresse</h3>
                    <p className="text-[#A1A1AA]">Passeig de Gràcia 100</p>
                    <p className="text-[#A1A1AA]">08008 Barcelona, España</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6">
              <h3 className="font-serif text-lg mb-2 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#D4AF37]" />
                Temps de réponse
              </h3>
              <p className="text-sm text-[#A1A1AA]">
                Nous nous efforçons de répondre à toutes les demandes dans un délai de 24 à 48 heures ouvrées.
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Google Auth Callback Handler
const GoogleAuthCallback = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double execution in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = location.hash.substring(1);
        const params = new URLSearchParams(hash);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error("Session ID manquant");
          navigate('/login');
          return;
        }

        // Exchange session_id for user data
        const response = await axios.post(`${API}/auth/google/session`, {
          session_id: sessionId
        }, {
          withCredentials: true  // Important for cookies
        });

        // Login with received token and user data
        login(response.data.token, response.data.user);
        toast.success(`Bienvenue ${response.data.user.full_name} !`);

        // Redirect to dashboard (remove hash from URL)
        navigate('/dashboard', { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error('Erreur authentification Google:', error);
        toast.error('Échec de la connexion Google');
        navigate('/login');
      }
    };

    processGoogleAuth();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
        <p className="text-lg">Connexion avec Google en cours...</p>
      </div>
    </div>
  );
};

// Main App
function App() {
  const [ageVerified, setAgeVerified] = useState(
    localStorage.getItem('age_verified') === 'true'
  );

  const handleAgeConfirm = () => {
    localStorage.setItem('age_verified', 'true');
    setAgeVerified(true);
  };

  return (
    <SecurityWrapper>
      <div className="App bg-[#050505] min-h-screen">
        <AuthProvider>
          <BrowserRouter>
            <GoogleAuthRouter />
          </BrowserRouter>
        </AuthProvider>
      </div>
    </SecurityWrapper>
  );
}

// Router component that handles Google OAuth callback
const GoogleAuthRouter = () => {
  const location = useLocation();
  
  // Check for Google OAuth callback BEFORE rendering normal routes
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }
  
  return <MainRoutes />;
};

// Main Routes Component
const MainRoutes = () => {
  const [ageVerified, setAgeVerified] = useState(
    localStorage.getItem('age_verified') === 'true'
  );

  const handleAgeConfirm = () => {
    localStorage.setItem('age_verified', 'true');
    setAgeVerified(true);
  };

  return (
    <>
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0F0F0F',
            color: '#EDEDED',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      />
      
      {!ageVerified && <AgeGate onConfirm={handleAgeConfirm} />}
      
      <Navigation />
      
      <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/articles" element={<ArticlesPage />} />
              <Route path="/videos" element={<VideosPage />} />
              <Route path="/forum" element={<ForumPage />} />
              <Route path="/referral" element={
                <ProtectedRoute>
                  <ReferralPage />
                </ProtectedRoute>
              } />
              <Route path="/become-creator" element={<BecomeCreatorPage />} />
            <Route path="/messages" element={
              <ProtectedRoute>
                <MessagesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            {/* Legal & Info Pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </>
  );
};


export default App;
